import { useState, useEffect, useRef, useCallback, useMemo } from "react"
import center from "@turf/center"
import { ParcelResultsPanel } from "./parcel-results-panel"
import { ParcelSearchForm } from "./parcel-search-form"
import { RecentParcelsList } from "./recent-parcels-list"
import { SingleParcelView } from "./single-parcel-view"
import { MultiParcelView } from "./multi-parcel-view"
import type { RecentParcel } from "@/types/explore"
import type { IParcel } from "@/types/explore"
import type { MapboxGeoJSONFeature } from "mapbox-gl"
import type { PluInfoType, POIMarker, MutationMarker } from "@/types/explore"

interface ParcelCardProps {
  selectedParcels: IParcel[]
  selectedPLU: MapboxGeoJSONFeature | null
  setSelectedParcels: React.Dispatch<React.SetStateAction<IParcel[]>>
  setParcelPOI: (poi: { lon: number | null; lat: number | null }) => void
  searchValue: string
  onSearchChange: (value: string) => void
  recentParcels: RecentParcel[]
  onSelectRecent: (parcel: RecentParcel) => void
  onCreateFolder: () => void
  searchResults: IParcel[]
  isSearching: boolean
  onSelectSearchResult: (parcel: IParcel) => void
  onClearSearch: () => void
  filtersEnabled: boolean
  setFiltersEnabled: (enabled: boolean) => void
  onFilterChange: (filter: {
    minParcelSurface: number
    maxParcelSurface: number
    ownerTypes: string[]
    siren: string
  }) => void
  parcelFilterResults: IParcel[]
  onSelectFilterResult: (parcel: IParcel) => void
  onHoverResult?: (lngLat: [number, number]) => void
  onLeaveResult?: () => void
  addRecentParcel?: (parcel: IParcel) => void
  removeRecentParcel?: (idu: string) => void
  onFlyTo?: (center: [number, number], zoom?: number) => void
  onPoiFlyTo?: (center: [number, number]) => void
  onActivePOIsChange?: (pois: POIMarker[]) => void
  onHoverPOI?: (poi: POIMarker | null) => void
  onActiveMutationsChange?: (markers: MutationMarker[]) => void
  isToolActive?: boolean
  // Data props for the selected parcel
  elevation?: number | null
  parcelOwnerType?: string | null
}

export const ParcelCard = ({
  selectedParcels,
  selectedPLU,
  setSelectedParcels,
  setParcelPOI,
  searchValue,
  onSearchChange,
  recentParcels,
  onSelectRecent,
  onCreateFolder,
  searchResults,
  isSearching,
  onSelectSearchResult,
  onClearSearch,
  filtersEnabled,
  setFiltersEnabled,
  onFilterChange,
  parcelFilterResults,
  onSelectFilterResult,
  onHoverResult,
  onLeaveResult,
  addRecentParcel,
  removeRecentParcel,
  onFlyTo,
  onPoiFlyTo,
  onActivePOIsChange,
  onHoverPOI,
  onActiveMutationsChange,
  isToolActive,
  elevation = null,
  parcelOwnerType = null,
}: ParcelCardProps) => {
  const [searchMode, setSearchMode] = useState<"reference" | "surface">("reference")
  const [surfaceMin, setSurfaceMin] = useState("")
  const [surfaceMax, setSurfaceMax] = useState("")
  const [ownerType, setOwnerType] = useState("Tous")
  const [siren, setSiren] = useState("")
  const [, setEffectivePLU] = useState<PluInfoType | null>(null)
  const [highlightedIndex, setHighlightedIndex] = useState(-1)
  const [openParcelIndices, setOpenParcelIndices] = useState<Set<number>>(new Set([0]))
  const inputRef = useRef<HTMLInputElement>(null)

  const firstOpenIndex = openParcelIndices.size > 0 ? Math.min(...Array.from(openParcelIndices)) : 0
  const selectedParcel =
    selectedParcels.length > 0 ? selectedParcels[Math.min(firstOpenIndex, selectedParcels.length - 1)] : null

  useEffect(() => {
    setOpenParcelIndices((prev) => {
      const max = Math.max(0, selectedParcels.length - 1)
      const next = new Set<number>()
      for (const idx of Array.from(prev)) {
        if (idx <= max) next.add(idx)
      }
      if (next.size === 0 && selectedParcels.length > 0) next.add(0)
      return next
    })
  }, [selectedParcels.length])

  const toggleParcel = useCallback((index: number) => {
    setOpenParcelIndices((prev) => {
      const next = new Set(prev)
      if (next.has(index)) next.delete(index)
      else next.add(index)
      return next
    })
  }, [])

  const toggleAll = useCallback(() => {
    setOpenParcelIndices((prev) => {
      if (prev.size === selectedParcels.length) return new Set()
      return new Set(selectedParcels.map((_, i) => i))
    })
  }, [selectedParcels])

  useEffect(() => {
    if (filtersEnabled) {
      onFilterChange({
        minParcelSurface: surfaceMin ? Number(surfaceMin) : 0,
        maxParcelSurface: surfaceMax ? Number(surfaceMax) : 0,
        ownerTypes: ownerType === "Tous" ? [] : [ownerType],
        siren,
      })
    }
  }, [surfaceMin, surfaceMax, ownerType, siren, filtersEnabled])

  const handleModeSwitch = (mode: "reference" | "surface") => {
    if (mode === searchMode) return
    setSearchMode(mode)
    if (mode === "surface") {
      setFiltersEnabled(true)
      onClearSearch()
    } else {
      setFiltersEnabled(false)
    }
  }

  useEffect(() => {
    setHighlightedIndex(-1)
  }, [searchResults])

  const handleClose = () => {
    setSelectedParcels([])
    setParcelPOI({ lon: null, lat: null })
  }

  const handleFlyTo = () => {
    if (!selectedParcel) return
    const coords = selectedParcel.center ?? center(selectedParcel.geometry)?.geometry?.coordinates
    if (coords?.length === 2) {
      onFlyTo?.(coords as [number, number], 18)
    }
  }

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (searchResults.length === 0) return
      if (e.key === "ArrowDown") {
        e.preventDefault()
        setHighlightedIndex((prev) => Math.min(prev + 1, searchResults.length - 1))
      } else if (e.key === "ArrowUp") {
        e.preventDefault()
        setHighlightedIndex((prev) => Math.max(prev - 1, 0))
      } else if (e.key === "Enter" && highlightedIndex >= 0) {
        e.preventDefault()
        onSelectSearchResult(searchResults[highlightedIndex])
      }
    },
    [searchResults, highlightedIndex, onSelectSearchResult],
  )

  const showSearchResults = searchResults.length > 0
  const showFilterResults = filtersEnabled && parcelFilterResults.length > 0
  const showNoResults = useMemo(
    () =>
      !isSearching &&
      searchValue.trim().replace(/\s+/g, "").length >= 6 &&
      searchResults.length === 0 &&
      /^[0-9A-Za-z\s]+$/.test(searchValue.trim()),
    [isSearching, searchValue, searchResults.length],
  )
  const showResultsPanel = showSearchResults || showFilterResults || showNoResults
  const showRecents = !selectedParcel && recentParcels.length > 0

  const handleSelectSearchResult = useCallback(
    (result: IParcel) => {
      onSelectSearchResult(result)
      onClearSearch()
    },
    [onSelectSearchResult, onClearSearch],
  )

  const resetFilters = () => {
    setSurfaceMin("")
    setSurfaceMax("")
    setOwnerType("Tous")
    setSiren("")
    handleModeSwitch("reference")
  }

  return (
    <div
      data-tour-step="parcel-card"
      className={`absolute top-2 left-2 z-40 flex items-start gap-2${isToolActive ? " pointer-events-none" : ""}`}
      style={{ bottom: "80px" }}
    >
      <div className="bg-white/95 backdrop-blur-sm map-panel-shadow rounded-xl w-80 flex flex-col max-h-full pointer-events-auto">
        {!selectedParcel && (
          <ParcelSearchForm
            searchMode={searchMode}
            onModeSwitch={handleModeSwitch}
            searchValue={searchValue}
            onSearchChange={onSearchChange}
            onClearSearch={onClearSearch}
            isSearching={isSearching}
            onKeyDown={handleKeyDown}
            inputRef={inputRef}
            surfaceMin={surfaceMin}
            surfaceMax={surfaceMax}
            onSurfaceMinChange={setSurfaceMin}
            onSurfaceMaxChange={setSurfaceMax}
            ownerType={ownerType}
            onOwnerTypeChange={setOwnerType}
            siren={siren}
            onSirenChange={setSiren}
          />
        )}

        {!selectedParcel && showRecents && (
          <RecentParcelsList
            recentParcels={recentParcels}
            onSelectRecent={onSelectRecent}
            onRemoveRecent={removeRecentParcel}
          />
        )}

        {selectedParcel && selectedParcels.length === 1 && (
          <SingleParcelView
            selectedParcel={selectedParcel}
            selectedPLU={selectedPLU}
            elevation={elevation}
            ownerType={parcelOwnerType}
            onFlyTo={handleFlyTo}
            onClose={handleClose}
            onCreateFolder={onCreateFolder}
            setParcelPOI={setParcelPOI}
            onPLULoaded={setEffectivePLU}
            onPoiFlyTo={onPoiFlyTo}
            onActivePOIsChange={onActivePOIsChange}
            onHoverPOI={onHoverPOI}
            onActiveMutationsChange={onActiveMutationsChange}
          />
        )}

        {selectedParcels.length > 1 && selectedParcel && (
          <MultiParcelView
            selectedParcels={selectedParcels}
            setSelectedParcels={setSelectedParcels}
            openParcelIndices={openParcelIndices}
            toggleParcel={toggleParcel}
            toggleAll={toggleAll}
            selectedPLU={selectedPLU}
            onClose={handleClose}
            onCreateFolder={onCreateFolder}
            setParcelPOI={setParcelPOI}
            onPLULoaded={setEffectivePLU}
            onPoiFlyTo={onPoiFlyTo}
            onActivePOIsChange={onActivePOIsChange}
            onHoverPOI={onHoverPOI}
            addRecentParcel={addRecentParcel}
            onActiveMutationsChange={onActiveMutationsChange}
          />
        )}
      </div>

      {showResultsPanel && (
        <ParcelResultsPanel
          searchResults={searchResults}
          showSearchResults={showSearchResults}
          showFilterResults={showFilterResults}
          showNoResults={showNoResults}
          highlightedIndex={highlightedIndex}
          parcelFilterResults={parcelFilterResults}
          onSelectSearchResult={handleSelectSearchResult}
          onSelectFilterResult={onSelectFilterResult}
          onHoverResult={onHoverResult}
          onLeaveResult={onLeaveResult}
          onResetFilters={resetFilters}
          onClose={resetFilters}
        />
      )}
    </div>
  )
}
